import { createSlice } from "@reduxjs/toolkit";

// Initial steps configuration
const INITIAL_STEPS = [
  { key: "caseDetails", label: "Case Details", component: "CaseDetailsStep" },
  { key: "evidence", label: "Evidence", component: "EvidenceStep" },
  {
    key: "victimDetails",
    label: "Victim Details",
    component: "VictimDetailsStep",
  },
  {
    key: "guardianDetails",
    label: "Guardian Details",
    component: "GuardianDetailsStep",
  },
  {
    key: "perpetratorDetails",
    label: "Perpetrator Details",
    component: "PerpetratorDetailsStep",
  },
  {
    key: "reporterDetails",
    label: "Reporter Details",
    component: "ReporterDetailsStep",
  },
  {
    key: "notificationContact",
    label: "Updates & Contact",
    component: "NotificationContactStep",
  },
  {
    key: "summaryReview",
    label: "Summary & Review",
    component: "SummaryReviewStep",
  },
  {
    key: "caseSuccess",
    label: "Case Submitted",
    component: "CaseSuccessStep",
  },
];

const initialState = {
  steps: INITIAL_STEPS,
  currentStepIndex: 0,
  formData: {},
  stepCompletionStatus: {}, // Track completion status separately to avoid unnecessary re-renders
  isSubmitting: false,
  isCompleted: false,
  isSuccessfullySubmitted: false,
  caseReferenceNumber: null,
  ncpaCaseNo: null,
  submissionDate: null,
  submissionError: null,
};

const formWizardSlice = createSlice({
  name: "formWizard",
  initialState,
  reducers: {
    updateSteps: (state, action) => {
      const stepsArray = action.payload;
      state.steps = stepsArray;
      // Reset to first step when steps change
      state.currentStepIndex = 0;
    },

    nextStep: (state) => {
      if (state.currentStepIndex < state.steps.length - 1) {
        state.currentStepIndex += 1;
      }
    },

    prevStep: (state) => {
      if (state.currentStepIndex > 0) {
        state.currentStepIndex -= 1;
      }
    },

    saveStepData: (state, action) => {
      const { stepName, values } = action.payload;
      state.formData[stepName] = values;

      // Update completion status
      const hasData = values && Object.keys(values).length > 0;
      state.stepCompletionStatus[stepName] = hasData;

      // Dynamic step logic based on Case Details
      if (stepName === "caseDetails") {
        const newSteps = filterStepsBasedOnCaseDetails(values, INITIAL_STEPS, state.formData);

        // Update steps
        state.steps = newSteps;

        // Ensure current step index is valid after filtering
        if (state.currentStepIndex >= newSteps.length) {
          state.currentStepIndex = newSteps.length - 1;
        }
      }

      // Re-evaluate steps when victim details are saved (for age-based filtering)
      if (stepName === "victimDetails" && state.formData.caseDetails) {
        const newSteps = filterStepsBasedOnCaseDetails(
          state.formData.caseDetails,
          INITIAL_STEPS,
          state.formData
        );

        // Update steps
        state.steps = newSteps;

        // Ensure current step index is valid after filtering
        if (state.currentStepIndex >= newSteps.length) {
          state.currentStepIndex = newSteps.length - 1;
        }
      }
    },

    updateStepField: (state, action) => {
      const { stepName, fieldName, value } = action.payload;

      // Initialize step data if it doesn't exist
      if (!state.formData[stepName]) {
        state.formData[stepName] = {};
      }

      // Update the specific field
      state.formData[stepName][fieldName] = value;

      // Update completion status only if it actually changes
      const hasData = state.formData[stepName] && Object.keys(state.formData[stepName]).length > 0;
      const currentStatus = state.stepCompletionStatus[stepName];
      if (currentStatus !== hasData) {
        state.stepCompletionStatus[stepName] = hasData;
      }

      // Dynamic step logic based on Case Details field changes
      if (stepName === "caseDetails") {
        const newSteps = filterStepsBasedOnCaseDetails(state.formData[stepName], INITIAL_STEPS, state.formData);

        // Update steps
        state.steps = newSteps;

        // Ensure current step index is valid after filtering
        if (state.currentStepIndex >= newSteps.length) {
          state.currentStepIndex = newSteps.length - 1;
        }
      }

      // Re-evaluate steps when victim details are updated (for age-based filtering)
      if (stepName === "victimDetails" && state.formData.caseDetails) {
        const newSteps = filterStepsBasedOnCaseDetails(
          state.formData.caseDetails,
          INITIAL_STEPS,
          state.formData
        );

        // Update steps
        state.steps = newSteps;

        // Ensure current step index is valid after filtering
        if (state.currentStepIndex >= newSteps.length) {
          state.currentStepIndex = newSteps.length - 1;
        }
      }
    },

    updateStepsBasedOnFormData: (state, action) => {
      const formData = action.payload || state.formData.caseDetails;
      if (formData) {
        const newSteps = filterStepsBasedOnCaseDetails(formData, INITIAL_STEPS, state.formData);

        // Update steps
        state.steps = newSteps;

        // Ensure current step index is valid after filtering
        if (state.currentStepIndex >= newSteps.length) {
          state.currentStepIndex = newSteps.length - 1;
        }
      }
    },

    submitFinal: (state) => {
      state.isSubmitting = true;
      // This would typically trigger an API call
      // For now, just mark as completed
      state.isCompleted = true;
    },

    setCaseReferenceNumber: (state, action) => {
      state.caseReferenceNumber = action.payload.caseReferenceNumber;
      state.ncpaCaseNo = action.payload.ncpaCaseNo || null;
      state.submissionDate = action.payload.submissionDate || new Date().toISOString();
      state.isSubmitting = false;
      state.isCompleted = true;
      state.isSuccessfullySubmitted = true;
      state.submissionError = null;
    },

    setSubmissionError: (state, action) => {
      state.isSubmitting = false;
      state.isCompleted = false;
      state.isSuccessfullySubmitted = false;
      state.submissionError = action.payload;
    },

    resetWizard: (state) => {
      state.steps = INITIAL_STEPS;
      state.currentStepIndex = 0;
      state.formData = {};
      state.stepCompletionStatus = {};
      state.isSubmitting = false;
      state.isCompleted = false;
      state.isSuccessfullySubmitted = false;
      state.caseReferenceNumber = null;
      state.ncpaCaseNo = null;
      state.submissionError = null;
      state.submissionDate = null;
    },

    setCurrentStep: (state, action) => {
      const stepIndex = action.payload;
      if (stepIndex >= 0 && stepIndex < state.steps.length) {
        state.currentStepIndex = stepIndex;
      }
    },
  },
});

// Helper function to filter steps based on case details and victim details
const filterStepsBasedOnCaseDetails = (caseData, allSteps, allFormData = {}) => {
  let filteredSteps = [...allSteps];
  const reportingFor = caseData?.reporting_for;
  const victimDetails = allFormData?.victimDetails;
  const victimAge = victimDetails?.victimAge ? parseInt(victimDetails.victimAge, 10) : null;

  // Step filtering based on "Who was affected" (reporting_for) value and age
  if (reportingFor) {
    switch (reportingFor) {
      case "Self / Victim":
        // When "Self / Victim" is selected:
        // - If victim age >= 18: Skip both Guardian Details and Reporter Details
        // - If victim age < 18: Keep Guardian Details, Skip Reporter Details
        // - If age not available yet: Use original logic (skip both for adults)
        if (victimAge !== null) {
          if (victimAge >= 18) {
            // Adult reporting for themselves
            filteredSteps = filteredSteps.filter(
              (step) => step.key !== "guardianDetails" && step.key !== "reporterDetails"
            );
          } else {
            // Minor reporting for themselves (needs guardian)
            filteredSteps = filteredSteps.filter(
              (step) => step.key !== "reporterDetails"
            );
          }
        } else {
          // Age not available yet - default to skip both (will re-evaluate when age is provided)
          filteredSteps = filteredSteps.filter(
            (step) => step.key !== "guardianDetails" && step.key !== "reporterDetails"
          );
        }
        break;

      case "Own Child":
        // When "Own Child" is selected:
        // - Skip Reporter Details (parent is the reporter and covered in guardian details)
        // - Keep Guardian Details (parent/guardian information)
        filteredSteps = filteredSteps.filter(
          (step) => step.key !== "reporterDetails"
        );
        break;

      case "Known Child":
        // When "Known Child" is selected:
        // - Keep Guardian Details (child needs guardian)
        // - Keep Reporter Details (separate from guardian)
        // - All steps are needed
        break;

      case "Known Adult":
        // When "Known Adult" is selected:
        // - Skip Guardian Details (not applicable for adults)
        // - Keep Reporter Details (reporter relationship to victim)
        filteredSteps = filteredSteps.filter(
          (step) => step.key !== "guardianDetails"
        );
        break;

      case "Unknown Person":
        // When "Unknown Person" is selected:
        // - Skip Guardian Details (not applicable)
        // - Keep Reporter Details (third party reporting)
        filteredSteps = filteredSteps.filter(
          (step) => step.key !== "guardianDetails"
        );
        break;

      default:
        // Keep all steps for unknown values
        break;
    }
  }

  // Additional filtering logic (existing)
  // If no guardian involved, remove guardian step
  if (caseData?.hasGuardian === "no" || caseData?.hasGuardian === false) {
    filteredSteps = filteredSteps.filter(
      (step) => step.key !== "guardianDetails"
    );
  }

  // If no known perpetrator, remove perpetrator step
  if (
    caseData?.hasKnownPerpetrator === "no" ||
    caseData?.hasKnownPerpetrator === false
  ) {
    filteredSteps = filteredSteps.filter(
      (step) => step.key !== "perpetratorDetails"
    );
  }

  // If victim is self-reporting, might skip reporter details
  if (
    caseData?.reporterType === "victim" ||
    caseData?.isSelfReporting === true
  ) {
    filteredSteps = filteredSteps.filter(
      (step) => step.key !== "reporterDetails"
    );
  }

  return filteredSteps;
};

// Selectors
export const selectCurrentStep = (state) => {
  const { steps, currentStepIndex } = state.formWizard;
  return steps[currentStepIndex] || null;
};

export const selectStepData = (stepName) => (state) => {
  return state.formWizard.formData[stepName] || {};
};

export const selectAllFormData = (state) => state.formWizard.formData;

export const selectStepCompletionStatus = (state) => state.formWizard.stepCompletionStatus;

export const selectIsFirstStep = (state) =>
  state.formWizard.currentStepIndex === 0;

export const selectIsLastStep = (state) => {
  const { steps, currentStepIndex } = state.formWizard;
  // Consider the summary review step as the last interactive step
  // The success step is not included in navigation logic
  const lastInteractiveStepIndex = steps.findIndex(step => step.key === "caseSuccess") - 1;
  return currentStepIndex === lastInteractiveStepIndex || currentStepIndex === steps.length - 1;
};

export const selectCanGoBack = (state) => {
  const isSuccessfullySubmitted = state.formWizard.isSuccessfullySubmitted;
  const currentStepIndex = state.formWizard.currentStepIndex;

  // If case has been successfully submitted, prevent going back from success step
  if (isSuccessfullySubmitted && state.formWizard.steps[currentStepIndex]?.key === 'caseSuccess') {
    return false;
  }

  return currentStepIndex > 0;
};

export const selectCanGoNext = (state) => {
  const { steps, currentStepIndex } = state.formWizard;
  return currentStepIndex < steps.length - 1;
};

export const selectIsSuccessfullySubmitted = (state) => state.formWizard.isSuccessfullySubmitted;

export const {
  updateSteps,
  nextStep,
  prevStep,
  saveStepData,
  updateStepField,
  updateStepsBasedOnFormData,
  submitFinal,
  resetWizard,
  setCurrentStep,
  setCaseReferenceNumber,
  setSubmissionError,
} = formWizardSlice.actions;

export default formWizardSlice.reducer;
